import { ChangeEvent, KeyboardEvent, useState, memo } from 'react';
import styled, { css } from 'styled-components';
import { IoEyeSharp, IoEyeOffSharp } from 'react-icons/io5';

import theme from '@styles/theme';

import { RegularFont } from './StyledComponents';

type StyledPasswordProps = {
    ICON?: boolean;
    id?: string;
    width?: string;
    height?: number;
    margin?: string;
    border?: string;
    borderRadius?: number;
    bgColor?: string;
    title?: string;
    name?: string;
    value?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    error?: boolean;
    maxLength?: number;
    disabled?: boolean;
    placeholder?: string;
};

const StyledPassword = ({
    ICON = true,
    id,
    width,
    height = 50,
    margin,
    border = `${theme.colors.ultraLightGrayBorder} 1px solid`,
    borderRadius = 4,
    bgColor = theme.colors.whiteColor,
    title,
    name,
    value,
    onChange,
    onKeyDown,
    error,
    maxLength = 200,
    disabled = false,
    placeholder
}: StyledPasswordProps) => {
    const [visible, setVisible] = useState(false);

    return (
        <>
            {title && <Title>{title}</Title>}
            <Wrapper
                $width={width}
                $height={height}
                $margin={margin}
                $border={border}
                $bgColor={bgColor}
                $borderRadius={borderRadius}
            >
                <Input
                    id={id}
                    name={name}
                    value={value}
                    type={visible ? 'text' : 'password'}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    maxLength={maxLength}
                    disabled={disabled}
                    placeholder={placeholder}
                    autoComplete="off"
                    $border={border}
                    $borderRadius={borderRadius}
                    $error={error}
                />
                {ICON && (
                    <ViewButton onClick={() => setVisible(!visible)}>
                        {visible ? (
                            <IoEyeOffSharp
                                fontSize={18}
                                color={theme.colors.lightGrayFontColor}
                            />
                        ) : (
                            <IoEyeSharp
                                fontSize={18}
                                color={theme.colors.lightGrayFontColor}
                            />
                        )}
                    </ViewButton>
                )}
            </Wrapper>
        </>
    );
};

export default memo(StyledPassword);

const Wrapper = styled.div<{
    $width?: string;
    $height?: number;
    $margin?: string;
    $border?: string;
    $bgColor?: string;
    $borderRadius?: number;
}>`
    position: relative;
    width: ${({ $width }) => ($width ? $width : '100%')};
    height: ${({ $height }) => $height}px;
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${({ $bgColor }) => $bgColor};
`;
const Title = styled(RegularFont)`
    margin-bottom: 8px;
    font-size: 14px;
`;
const Input = styled.input<{
    $error?: boolean;
    $border?: string;
    $borderRadius?: number;
}>`
    width: 100%;
    height: 100%;
    padding: 14px 40px 14px 12px;
    font-size: 15px;
    background-color: inherit;
    border: ${({ $border }) => $border};
    border-radius: ${({ $borderRadius }) => $borderRadius}px;
    ${({ $error }) =>
        $error &&
        css`
            &:focus {
                border: ${theme.colors.activePink} 1px solid;
            }
        `};
`;
const ViewButton = styled.div`
    position: absolute;
    bottom: 15px;
    right: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`;
